"use client";

import { useCallback, useState, useTransition } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/format";
import { pathForSync } from "@/lib/routing-shared";
import { PlacesCard } from "@/components/card/PlacesCard";
import type { AttractionList, Pagination } from "@/lib/dto";
import { Button } from "../ui/Button";

type Props = {
  initialItems: AttractionList[];
  initialPagination: Pagination;
};

export function AttractionListClient({
  initialItems,
  initialPagination,
}: Props) {
  const [items, setItems] = useState<AttractionList[]>(initialItems);
  const [pagination, setPagination] = useState<Pagination>(initialPagination);
  const [isPending, startTransition] = useTransition();

  const loadMore = useCallback(() => {
    startTransition(async () => {
      const p = new URLSearchParams({
        page: String(pagination.page + 1),
        limit: String(pagination.limit),
      });
      const res = await fetch(`/api/attractions?${p}`);
      const data = await res.json();
      setItems((prev) => [...prev, ...data.items]);
      setPagination(data.pagination);
    });
  }, [pagination.page, pagination.limit]);

  return (
    <div>
      <ul
        className={cn(
          "grid gap-6 sm:grid-cols-2 lg:grid-cols-3",
          isPending && "opacity-60",
        )}
      >
        {items.map((attraction) => (
          <li key={attraction.id}>
            <PlacesCard
              attraction={attraction}
              href={pathForSync("attraction", attraction.slug)}
            />
          </li>
        ))}
      </ul>

      {pagination.has_next ? (
        <div className="mt-6 text-center">
          <Button
            type="button"
            onClick={loadMore}
            disabled={isPending}
            variant="primary"
            className="disabled:opacity-60"
          >
            {isPending ? (
              <Loader2 size={16} className="animate-spin" aria-hidden />
            ) : null}
            Load More
          </Button>
        </div>
      ) : null}
    </div>
  );
}
